/**
 * Horizontal slats as exact circle chords in viewBox space (avoids raster warp from unified grids).
 */

/**
 * @param {{ width: number, height: number }} viewBox
 * @param {object} layoutOptions - padding, barFillRatio, opacity (same defaults as mapSegmentsToViewBox)
 * @param {number} rowCount - number of horizontal scan rows across the circle diameter
 * @param {{ r: number }} circle - radius in the same units as viewBox (e.g. 58 in a 200×200 mask)
 * @returns {{ x: number, y: number, w: number, h: number, rx: number, opacity: number }[]}
 */
export function circleChordSlatRects(viewBox, layoutOptions, rowCount, circle) {
  const vbW = viewBox.width;
  const vbH = viewBox.height;
  const padding = layoutOptions.padding ?? 4;
  const barFillRatio = layoutOptions.barFillRatio ?? 0.42;
  const opacity = layoutOptions.opacity ?? 1;
  const rNominal = circle.r;

  const innerW = vbW - 2 * padding;
  const innerH = vbH - 2 * padding;
  const d = 2 * rNominal;
  const scale = Math.min(innerW / d, innerH / d);
  const R = rNominal * scale;
  const cxS = vbW / 2;
  const cyS = vbH / 2;
  const drawn = 2 * R;
  const originY = cyS - R;
  const rows = Math.max(1, Math.round(rowCount));
  const rowPitch = drawn / rows;
  const barH = rowPitch * barFillRatio;

  const rects = [];
  for (let k = 0; k < rows; k += 1) {
    const yRowCenter = originY + (k + 0.5) * rowPitch;
    const dy = yRowCenter - cyS;
    if (Math.abs(dy) >= R) {
      continue;
    }
    const halfChord = Math.sqrt(R * R - dy * dy);
    const wSvg = 2 * halfChord;
    const xSvg = cxS - halfChord;
    const ySvg = yRowCenter - barH / 2;
    const rx = Math.min(barH / 2, wSvg / 2);
    rects.push({ x: xSvg, y: ySvg, w: wSvg, h: barH, rx, opacity });
  }

  return rects;
}
